import { useState } from "react";
import { useImageInput } from "../../../../hooks";
import { DefaultAvatar } from "../../../../assets/images";
import { usePssAprsLogoAddRepo, usePssLogoSubmitRepo } from "../repositories";

const defaultForm = {
  insignia: DefaultAvatar,
};

const usePssLogoPicture = () => {
  const [validated, setValidated] = useState<boolean>(false);
  const [invalidFormMsg, setInvalidFormMsg] = useState<string | null>(null);
  const [apiInsertShowAlert, setApiInsertShowAlert] = useState<boolean>(false);
  const [showSuccessToast, setShowSuccessToast] = useState<boolean>(false);
  const [successToastMessage, setSuccessToastMessage] = useState<string>("");

  // Repositories
  const [apiLogoRepo, _apiPssLogoSubmit] = usePssLogoSubmitRepo();
  const [_apiLogoAddRepo, apiPssLogoAdd] = usePssAprsLogoAddRepo();

  // Image/Avatar/Insignia Input Field
  const [insignia, insigniaController] = useImageInput(defaultForm.insignia, {
    validator: (extension) => {
      if (extension === undefined) {
        return {
          valid: false,
          feedback: "",
        };
      }
      if (extension !== "png" && extension !== "jpeg" && extension !== "jpg") {
        return {
          valid: false,
          feedback: `Unsupported Format (${extension})`,
        };
      }
      return {
        valid: true,
        feedback: "",
      };
    },
    validated: validated,
    onFocus: () => {
      setInvalidFormMsg(null);
      setApiInsertShowAlert(false);
    },
  });

  // actions

  const showSuccessToastNotification = (message: string) => {
    setSuccessToastMessage(message);
    setShowSuccessToast(true);
    setTimeout(() => {
      setShowSuccessToast(false);
    }, 1000);
  };

  const resetForm = () => {
    setValidated(false);
    insigniaController.resetImage();
    setInvalidFormMsg(null);
    setApiInsertShowAlert(false);
  };

  const submitForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setValidated(true);

    if (insigniaController.peek().invalid) {
      setInvalidFormMsg("Invalid form, please provide a valid input (Logo) !");
      return;
    } else {
      const formData = new FormData();
      formData.append("file", insignia.imageFile as Blob);

      apiPssLogoAdd(formData)
        .then(() => {
          showSuccessToastNotification("New account added!");
        })
        .catch((error: unknown) => {
          console.error("Error adding action:", error);
          setApiInsertShowAlert(true);
        });
    }
  };

  let apiInsertErrorMsg =
    apiLogoRepo.error?.message ?? "Failed to submit the new logo!";
  if (apiLogoRepo.duplicate) {
    apiInsertErrorMsg = `The ${apiLogoRepo.duplicate.key} '${apiLogoRepo.duplicate.value}' is already in use`;
  }

  return [
    {
      mode: "Submit",
      insignia,
      showSuccessToast,
      successToastMessage,
      statusDisabled: true,
      invalidFormMsg,
      data: apiLogoRepo.data,
      apiInsertLoading: apiLogoRepo.isLoading,
      apiInsertError: apiLogoRepo.isError && apiInsertShowAlert,
      apiInsertErrorMsg: apiInsertErrorMsg,
      apiInsertSuccess: apiLogoRepo.isSuccess && apiInsertShowAlert,
    },
    {
      insignia: insigniaController,
      submitForm,
      resetForm,
      showSuccessToastNotification,
    },
  ] as const;
};

export default usePssLogoPicture;
